import { useEffect, useRef, useState } from 'react';

import { AuthService } from '../../../js/api';
import {
  markSessionActivity,
  SESSION_LAST_ACTIVITY_STORAGE_KEY,
} from '../../../js/api/core/request';
import { localStore } from '../utils/storage';
import { Button } from './button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from './dialog';

/** Warn this long before the predicted idle logout. */
const WARNING_LEAD_MS = 2 * 60 * 1000;
/** Slow poll while the session is comfortably alive. */
const IDLE_TICK_MS = 15 * 1000;
/** Fast poll while the countdown dialog is visible. */
const COUNTDOWN_TICK_MS = 1000;

export type SessionExpiryWarningProps = {
  /** Fired when the idle window has fully elapsed — redirect to login. */
  onExpired: () => void;
  /** Fired when the user chooses to log out from the warning. */
  onLogout: () => void;
};

/**
 * Warns the user before the rolling idle timeout (SESSION_COOKIE_AGE) logs
 * them out. The prediction mirrors the server's idle clock: every successful
 * API response rolls the server session and stamps
 * SESSION_LAST_ACTIVITY_STORAGE_KEY (shared across tabs), so
 * expiry ≈ last activity + SESSION_COOKIE_AGE. "Stay signed in" makes an
 * authenticated request, which rolls the real session. Countdown zero logs
 * out server-side BEFORE redirecting — the prediction can fire while the
 * session is still alive, and a bare redirect would let the login page's
 * session probe roll the session and bounce right back into the app.
 *
 * Renders nothing when window.SESSION_COOKIE_AGE is not provided.
 */
export function SessionExpiryWarning({ onExpired, onLogout }: SessionExpiryWarningProps) {
  const sessionAgeMs = (window.SESSION_COOKIE_AGE || 0) * 1000;
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isExtending, setIsExtending] = useState(false);
  // Floor for the idle clock: guards against a stale cross-tab timestamp from
  // a previous session firing onExpired immediately on mount.
  const mountedAtRef = useRef(Date.now());
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!sessionAgeMs) return undefined;

    const tick = () => {
      const stored = Number(localStore.get(SESSION_LAST_ACTIVITY_STORAGE_KEY)) || 0;
      const lastActivity = Math.max(stored, mountedAtRef.current);
      const remainingMs = lastActivity + sessionAgeMs - Date.now();

      if (remainingMs <= 0) {
        if (!expiredFiredRef.current) {
          expiredFiredRef.current = true;
          // The prediction can run slightly ahead of the server's idle clock
          // (activity stamps are throttled, and non-axios requests roll the
          // session without stamping), so the session may still be alive here.
          // A bare redirect then boomerangs: the login page's session probe
          // rolls the still-alive session and bounces straight back into the
          // app. Log out server-side first so countdown zero is authoritative,
          // then hand off to onExpired for the login redirect either way.
          AuthService.authLogoutCreate()
            .catch(() => undefined)
            .then(() => onExpired());
        }
      } else if (remainingMs <= WARNING_LEAD_MS) {
        setSecondsLeft(Math.ceil(remainingMs / 1000));
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    tick();
    const id = window.setInterval(tick, open ? COUNTDOWN_TICK_MS : IDLE_TICK_MS);
    return () => window.clearInterval(id);
  }, [sessionAgeMs, open, onExpired]);

  const staySignedIn = async () => {
    setIsExtending(true);
    try {
      // Any authenticated request rolls the server-side session; the response
      // interceptor stamps activity, but stamp explicitly so the dialog can
      // close deterministically even within the interceptor's throttle window.
      await AuthService.authUserRetrieve();
      markSessionActivity();
      localStore.set(SESSION_LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      setOpen(false);
    } catch {
      // Session already gone — the axios interceptor redirects to /login.
    } finally {
      setIsExtending(false);
    }
  };

  if (!open) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, '0');

  return (
    <Dialog open onClose={staySignedIn} size="md">
      <DialogTitle>Your session is about to expire</DialogTitle>
      <DialogBody>
        <p className="tw:text-zinc-700" data-testid="session-expiry-countdown">
          You will be logged out in {minutes}:{seconds} due to inactivity, and any unsaved changes
          will be lost.
        </p>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="light" onClick={onLogout} data-testid="session-expiry-logout">
          Log out
        </Button>
        <Button
          type="button"
          color="brandgreen0"
          onClick={staySignedIn}
          disabled={isExtending}
          data-testid="session-expiry-stay"
        >
          {isExtending ? 'One moment...' : 'Stay signed in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
